import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { PublicPage, APP_NAME } from '@/components/public-page';
import { IS_HOOFDNAARHART, LOGIN_PATH, supportEmail } from '@/lib/constants';

/** What the daily purge waits before removing an account for good. */
const RETENTION_DAYS = 30;

/**
 * How to delete your account, reachable without signing in.
 *
 * Google Play requires a web URL where account deletion can be requested, and
 * it has to work for someone who cannot sign in any more -- which is exactly
 * the person the in-app route does not serve. Apple asks for the same thing.
 *
 * The numbers here follow purge_deleted_profiles(): deleting sets deleted_at,
 * and a daily cron removes the account thirty days later. Saying anything
 * else would be describing a product we do not have.
 */
export default function AccountVerwijderenPage() {
  const [email, setEmail] = useState(supportEmail);

  useEffect(() => {
    const fetchEmail = async () => {
      const key = IS_HOOFDNAARHART ? 'hnh_contact_email' : 'contact_email';
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', key)
        .maybeSingle();
      if (data?.value) setEmail(data.value);
    };

    fetchEmail();
  }, []);

  return (
    <PublicPage
      title="Account verwijderen"
      description={`Hoe je je account en gegevens in de ${APP_NAME} app laat verwijderen, in de app of per e-mail.`}
    >
      <div className="space-y-6">
        <section>
          <h2 className="font-medium">In de app</h2>
          <p className="mt-1 text-muted-foreground">
            Log in en ga naar <strong>Profiel</strong>. Onderaan die pagina staat{' '}
            <strong>Account verwijderen</strong>. Je wordt om bevestiging
            gevraagd en daarna meteen uitgelogd.
          </p>
        </section>

        <section>
          <h2 className="font-medium">Lukt inloggen niet meer?</h2>
          <p className="mt-1 text-muted-foreground">
            Mail ons vanaf het e-mailadres waarmee je account is aangemaakt, dan
            verwijderen wij het voor je.
          </p>
          {email && (
            <a
              href={`mailto:${email}?subject=${encodeURIComponent('Verzoek account verwijderen')}`}
              className="mt-3 inline-flex items-center gap-2 font-medium text-green-700 underline underline-offset-4 hover:text-green-800"
            >
              <Mail className="h-4 w-4" />
              {email}
            </a>
          )}
        </section>

        <section>
          <h2 className="font-medium">Wat er daarna gebeurt</h2>
          <p className="mt-1 text-muted-foreground">
            Je account wordt direct gesloten: inloggen kan niet meer en je bent
            niet langer zichtbaar voor je team of groep. Na {RETENTION_DAYS}{' '}
            dagen wordt alles definitief verwijderd — je profiel, je naam en
            e-mailadres, je favorieten, je luistergeschiedenis en je lidmaatschap
            van teams en groepen. Die periode is er zodat een verwijdering die
            per ongeluk is gedaan nog teruggedraaid kan worden; neem daarvoor
            binnen {RETENTION_DAYS} dagen contact op.
          </p>
        </section>

        <section>
          <h2 className="font-medium">Berichten in een team of groep</h2>
          <p className="mt-1 text-muted-foreground">
            Berichten die je in een teamchat of samen-groep hebt geplaatst
            kunnen bewaard blijven voor de andere deelnemers aan dat gesprek.
            Wil je die ook verwijderd hebben, vermeld dat dan in je mail.
          </p>
        </section>

        <section>
          <h2 className="font-medium">Meer weten</h2>
          <p className="mt-1 text-muted-foreground">
            In ons{' '}
            <Link to="/privacy" className="underline underline-offset-4">
              privacybeleid
            </Link>{' '}
            staat welke gegevens we bewaren en waarom. Vragen kun je stellen via{' '}
            <Link to="/support" className="underline underline-offset-4">
              support
            </Link>
            , en{' '}
            <Link to={LOGIN_PATH} className="underline underline-offset-4">
              inloggen
            </Link>{' '}
            kan hier.
          </p>
        </section>
      </div>
    </PublicPage>
  );
}
